import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { ReportDefinition, ReportFolder, TemplateFragment } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { NavIcon } from "../../components/NavIcon";

interface FolderNode extends ReportFolder {
  children: FolderNode[];
}

function buildTree(folders: ReportFolder[]): FolderNode[] {
  const nodes = new Map<string, FolderNode>(
    folders.map((f) => [f.id, { ...f, children: [] }])
  );
  const roots: FolderNode[] = [];
  for (const node of nodes.values()) {
    if (node.parentFolderId && nodes.has(node.parentFolderId)) {
      nodes.get(node.parentFolderId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export default function FolderManager() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [folders, setFolders] = useState<ReportFolder[]>([]);
  const [fragments, setFragments] = useState<TemplateFragment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReportFolder | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ReportFolder | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [parentFolderId, setParentFolderId] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [headerFragmentId, setHeaderFragmentId] = useState("");
  const [footerFragmentId, setFooterFragmentId] = useState("");
  const [watermarkFragmentId, setWatermarkFragmentId] = useState("");

  function load() {
    setLoading(true);
    Promise.all([adminApi.folders.list(), adminApi.fragments.list(), adminApi.reports.list()])
      .then(([f, frag, r]) => {
        setFolders(f);
        setFragments(frag);
        setReports(r);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load folders"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function resetForm() {
    setSelected(null);
    setName("");
    setDescription("");
    setParentFolderId("");
    setIsShared(false);
    setHeaderFragmentId("");
    setFooterFragmentId("");
    setWatermarkFragmentId("");
  }

  function editFolder(folder: ReportFolder) {
    setSelected(folder);
    setName(folder.name);
    setDescription(folder.description ?? "");
    setParentFolderId(folder.parentFolderId ?? "");
    setIsShared(folder.shared);
    setHeaderFragmentId(folder.headerFragmentId ?? "");
    setFooterFragmentId(folder.footerFragmentId ?? "");
    setWatermarkFragmentId(folder.watermarkFragmentId ?? "");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      name,
      description: description || null,
      parentFolderId: parentFolderId || null,
      shared: isShared,
      headerFragmentId: headerFragmentId || null,
      footerFragmentId: footerFragmentId || null,
      watermarkFragmentId: watermarkFragmentId || null,
    };
    try {
      if (selected) {
        await adminApi.folders.update(selected.id, body);
      } else {
        await adminApi.folders.create(body);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save folder");
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await adminApi.folders.remove(pendingDelete.id);
      setPendingDelete(null);
      if (selected?.id === pendingDelete.id) resetForm();
      load();
    } catch (err) {
      setPendingDelete(null);
      setError(err instanceof Error ? err.message : "Failed to delete folder");
    }
  }

  function renderNode(node: FolderNode, depth = 0) {
    return (
      <div key={node.id}>
        <div
          className={`folder-row folder-row-link${selected?.id === node.id ? " folder-row-selected" : ""}`}
          style={{ marginLeft: depth * 28 }}
          role="link"
          tabIndex={0}
          title={`Open ${node.name}`}
          onClick={() => navigate(`/folders/${node.id}`)}
          onKeyDown={(e) => e.key === "Enter" && navigate(`/folders/${node.id}`)}
        >
          <div className="folder-row-icon">
            <NavIcon name="folder" size={17} />
          </div>
          <div className="folder-row-main">
            <div className="folder-row-name">
              {node.name}
              {node.shared && <span className="badge badge-published">shared</span>}
            </div>
            {node.description && <div className="folder-row-desc">{node.description}</div>}
          </div>
          <span className="badge" title="Reports in this folder">
            {reports.filter((r) => r.folderId === node.id).length} report
            {reports.filter((r) => r.folderId === node.id).length === 1 ? "" : "s"}
          </span>
          <div className="folder-row-actions" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn-secondary" onClick={() => editFolder(node)}>
              Edit
            </button>
            <button type="button" className="btn-danger" onClick={() => setPendingDelete(node)}>
              Delete
            </button>
          </div>
          <span className="folder-row-chevron" aria-hidden="true">›</span>
        </div>
        {node.children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  }

  const tree = buildTree(folders);
  const fragmentsByType = (type: string) => fragments.filter((f) => f.type === type);

  return (
    <div>
      <div className="page-header">
        <h1>Folders</h1>
      </div>
      {error && <div className="error-text">{error}</div>}

      <div className="split-layout">
        <div className="page-panel">
          <h3 style={{ marginTop: 0 }}>All folders</h3>
          {loading ? <p>Loading...</p> : tree.length === 0 ? (
            <div className="empty-state">No folders yet. Create one to organize your reports.</div>
          ) : (
            tree.map((node) => renderNode(node))
          )}
        </div>

        <form className="page-panel split-aside" onSubmit={handleSubmit}>
          <h3>{selected ? `Edit "${selected.name}"` : "New folder"}</h3>

          <div className="form-row">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div className="form-row">
            <label>Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="form-row">
            <label>Parent folder</label>
            <select value={parentFolderId} onChange={(e) => setParentFolderId(e.target.value)}>
              <option value="">(top level)</option>
              {folders
                .filter((f) => f.id !== selected?.id)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-row">
            <label>
              <input
                type="checkbox"
                checked={isShared}
                onChange={(e) => setIsShared(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Shared / global (visible read-only to all tenants)
            </label>
          </div>

          <div className="form-row">
            <label>Header fragment</label>
            <select value={headerFragmentId} onChange={(e) => setHeaderFragmentId(e.target.value)}>
              <option value="">(none)</option>
              {fragmentsByType("HEADER").map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label>Footer fragment</label>
            <select value={footerFragmentId} onChange={(e) => setFooterFragmentId(e.target.value)}>
              <option value="">(none)</option>
              {fragmentsByType("FOOTER").map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label>Watermark fragment</label>
            <select value={watermarkFragmentId} onChange={(e) => setWatermarkFragmentId(e.target.value)}>
              <option value="">(none)</option>
              {fragmentsByType("WATERMARK").map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="submit" className="btn-primary">
              {selected ? "Save changes" : "Create folder"}
            </button>
            {selected && (
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete folder"
        message={`Delete "${pendingDelete?.name}"? Reports inside will need to be reassigned.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
